import React, { useState } from 'react'
import { Button, Form, Alert, Row, Col } from 'react-bootstrap'
import BigSpinner from 'common/components/BigSpinner'
import { Formik } from 'formik'
import { DataTable, ColumnDefinition } from 'common/components/DataTable'
import * as yup from 'yup'
import Utils from 'common/Utils'
import API from 'common/logic/API'
import { Navigate } from 'react-router-dom'

type Direction = 'BUY' | 'SELL'

type ParsedTransaction = {
    date: string
    financialAssetId: number
    financialAssetName: string
    euroAmount: string
    assetAmount: string
}

type ParseResult = {
    direction: Direction
    transactions: ParsedTransaction[]
}

const ProcessState = Object.freeze({
    "WAITING_INPUT": 1,
    "PARSING": 2,
    "WAITING_REVIEW": 3,
    "EXECUTING": 4,
    "DONE": 5,
    "ERROR": 6,
})

interface ReviewFormProps {
    parsed: ParseResult
    onExecuting: () => void
    onDone: () => void
    onError: (msg: string) => void
}

function ReviewForm({ parsed, onExecuting, onDone, onError }: ReviewFormProps) {
    const direction = parsed.direction
    const isBuy = direction === 'BUY'

    const schema = yup.object().shape({
        import: yup.array(),
    })

    const initialValues = {
        import: parsed.transactions.map(() => true),
    }

    const submit = (values: any) => {
        onExecuting()
        const payload = parsed.transactions
            .map((tx: ParsedTransaction) => ({
                date: tx.date,
                financialAssetId: tx.financialAssetId,
                assetAmount: isBuy ? `+${tx.assetAmount}` : `-${tx.assetAmount}`,
                euroAmount: isBuy ? `-${tx.euroAmount}` : `+${tx.euroAmount}`,
                notes: null,
            }))
            .filter((_t: any, i: number) => values.import[i])

        API.post("/asset-transactions/batch", payload)
            .then(() => onDone())
            .catch((e: any) => onError(e?.response?.data?.message ?? e.message))
    }

    const definitions = (handleChange: any, values: any) => [
        new ColumnDefinition("Import", (_obj: any, i: number) => (
            <Form.Check
                type="checkbox"
                label=""
                name={`import[${i}]`}
                checked={values.import[i]}
                onChange={handleChange}
            />
        )),
        new ColumnDefinition("Date", "date"),
        new ColumnDefinition("Asset", "financialAssetName"),
        new ColumnDefinition("Direction", () => isBuy ? 'Buy' : 'Sell', 'td-size-s'),
        new ColumnDefinition("Asset amount", (obj: ParsedTransaction) =>
            <>{Utils.formatPreciseAmount(obj.assetAmount)}</>, 'td-currency', 'td-right'
        ),
        new ColumnDefinition("Euro amount", (obj: ParsedTransaction) =>
            <>{Utils.formatMoney(obj.euroAmount, true)} €</>, 'td-currency', 'td-right'
        ),
    ]

    return <>
        <Alert variant="success">
            Parsed <strong>{parsed.transactions.length}</strong> {isBuy ? 'buy' : 'sell'} transaction{parsed.transactions.length === 1 ? '' : 's'}.
            Adjust the selection and confirm the import below.
        </Alert>
        <Formik
            validationSchema={schema}
            onSubmit={submit}
            initialValues={initialValues}
        >
            {({ handleSubmit, handleChange, values }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <DataTable
                        columns={definitions(handleChange, values)}
                        rows={parsed.transactions}
                    />
                    <Button variant="primary" type="submit">Insert {parsed.transactions.length} transaction{parsed.transactions.length === 1 ? '' : 's'}</Button>
                </Form>
            )}
        </Formik>
    </>
}

function ImportAssetTransactions() {
    const [step, setStep] = useState<number>(ProcessState.WAITING_INPUT)
    const [error, setError] = useState<React.ReactNode | null>(null)
    const [parsed, setParsed] = useState<ParseResult | null>(null)
    const [lastText, setLastText] = useState('')
    const [lastDirection, setLastDirection] = useState<Direction>('BUY')

    const parseForm = (values: any) => {
        setError(null)
        setLastText(values.text)
        setLastDirection(values.direction)
        setStep(ProcessState.PARSING)
        API.post("/asset-transactions/statement", { text: values.text, direction: values.direction })
            .then((response: any) => {
                setParsed(response.data)
                setStep(ProcessState.WAITING_REVIEW)
            }).catch((e: any) => {
                const label = e?.response?.data?.label
                const message = e?.response?.data?.message ?? e.message
                if (label === "statement-parsing-failed" || label === "invalid-parameter") {
                    setStep(ProcessState.WAITING_INPUT)
                    setError(message)
                    setParsed(null)
                } else {
                    setStep(ProcessState.ERROR)
                    setError(message)
                }
            })
    }

    const fileForm = () => {
        const schema = yup.object().shape({
            text: yup.string().required('Please paste the transactions text'),
            direction: yup.string().oneOf(['BUY', 'SELL']).required(),
        })

        return <>
            <Alert variant="info">
                Paste an Excel selection of transactions. Each row must be tab-separated with columns:
                <strong> Date &#9; Asset &#9; Amount Euro &#9; Amount items </strong>
                (any further columns, e.g. Price, are ignored). A header row is optional.
            </Alert>
            <Formik
                onSubmit={parseForm}
                initialValues={{ text: lastText, direction: lastDirection }}
                validationSchema={schema}
                enableReinitialize
            >
                {({ handleSubmit, handleChange, values, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Group as={Row} controlId="directionControl" className="mb-3">
                            <Form.Label column sm="2">Direction</Form.Label>
                            <Col>
                                <div key="inline-radio" className="mb-2">
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="Buy"
                                        name="direction"
                                        id="direction-buy"
                                        value="BUY"
                                        checked={values.direction === 'BUY'}
                                        onChange={handleChange}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="Sell"
                                        name="direction"
                                        id="direction-sell"
                                        value="SELL"
                                        checked={values.direction === 'SELL'}
                                        onChange={handleChange}
                                    />
                                </div>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="textControl" className="mb-3">
                            <Form.Label column sm="2">Transactions</Form.Label>
                            <Col>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    name="text"
                                    value={values.text}
                                    onChange={handleChange}
                                    isInvalid={!!errors.text}
                                    placeholder={'Date\tAsset\tAmount Euro\tAmount items\tPrice\n2017-06-12\tCrypto: ETH\t€750.00\t2.36369\t€317.30'}
                                />
                                <Form.Control.Feedback type="invalid">{errors.text as string}</Form.Control.Feedback>
                            </Col>
                        </Form.Group>
                        <Button variant="primary" type="submit">Parse</Button>
                    </Form>
                )}
            </Formik>
        </>
    }

    const reviewForm = () => {
        if (!parsed || parsed.transactions.length === 0) {
            return <Alert variant="warning">No transactions parsed</Alert>
        }
        return <ReviewForm
            parsed={parsed}
            onExecuting={() => setStep(ProcessState.EXECUTING)}
            onDone={() => setStep(ProcessState.DONE)}
            onError={(msg: string) => {
                setStep(ProcessState.ERROR)
                setError(msg)
            }}
        />
    }

    const renderBody = () => {
        switch (step) {
            case ProcessState.WAITING_INPUT:
            case ProcessState.WAITING_REVIEW:
                return <>
                    {fileForm()}
                    <hr />
                    {step === ProcessState.WAITING_REVIEW && reviewForm()}
                </>
            case ProcessState.PARSING:
                return <BigSpinner />
            case ProcessState.EXECUTING:
                return <BigSpinner />
            case ProcessState.ERROR:
                return <></>
            case ProcessState.DONE:
                return <Navigate to="/investments/transactions" replace />
            default:
                return <></>
        }
    }

    return <>
        <h1>Import asset transactions</h1>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {renderBody()}
    </>
}

export default ImportAssetTransactions